import { useCallback } from 'react';
import { useCreateCasinoSessionMutation } from '@/store/api/casinoApi';
import { getPlatform, isGuestUser } from '@/utils';
import { useAppDispatch } from '@/hooks';
import { useIsDemoUser } from '@/hooks/redux';
import { launchGame } from '@/store';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { setLoginModalOpen } from '@/store/slices/uiSlice';

export const useCasinoGameLauncher = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [createSession] = useCreateCasinoSessionMutation();
  const isDemoUser = useIsDemoUser();

  const launchCasinoGame = useCallback(
    async (gameId: number, _providerName: string) => {
      try {
        if (isGuestUser()) {
          dispatch(setLoginModalOpen(true));
          return;
        }

        if (isDemoUser) {
          toast.error('Demo users are not allowed to play casino games.', {
            id: 'demo-casino-error',
          });
          return;
        }

        const payload = {
          id: gameId,
          platform: getPlatform(),
        };
        const res = await createSession(payload).unwrap();

        if (res?.url) {
          dispatch(launchGame(res.url));
          navigate('/casino-playground', {
            state: {
              gameUrl: res.url,
            },
          });
        } else if (res?.success === false) {
          toast.dismiss();
          toast.error(res?.message || 'Failed to get game URL', { id: 'casino-launch-success' });
        }
      } catch (error: any) {
        toast.dismiss();
        toast.error(error?.data?.message || 'Failed to create session', {
          id: 'casino-launch-error',
        });

        // console.error('Failed to launch game', error);
      }
    },
    [createSession, dispatch, isDemoUser, navigate]
  );

  return {
    launchCasinoGame,
  };
};
